import { createLogger } from "@/lib/logger";
import type { Room } from "livekit-client";
import { useCallback, useEffect, useRef, useState } from "react";

const logger = createLogger("whiteboard-practice-selection");
export const WHITEBOARD_SELECTION_DURATION_MS = 2000;

/** Acknowledge the selected source, independently of transport and board outcomes. */
export function useWhiteboardPracticeSelection(room: Room | null) {
  const [selection, setSelection] = useState<{
    room: Room;
    targetKey: string;
    id: number;
  } | null>(null);
  const sequence = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [room]
  );

  const selectForPractice = useCallback(
    async (targetKey: string, messageText?: string) => {
      if (!room) return;
      const id = ++sequence.current;
      if (timer.current !== null) clearTimeout(timer.current);
      setSelection({ room, targetKey, id });
      timer.current = setTimeout(() => {
        timer.current = null;
        setSelection((current) => (current?.id === id ? null : current));
      }, WHITEBOARD_SELECTION_DURATION_MS);

      // Keep the established worker command format, with the complete selected turn.
      // This is an application action, so it does not create a typed transcript row.
      const selectedMessage = messageText?.trim().replace(/\s+/g, " ");
      const request = selectedMessage
        ? `Create a whiteboard exercise so I can practice this: "${selectedMessage}"`
        : "Create a whiteboard exercise based on our recent conversation.";
      try {
        await room.localParticipant.sendText(request, { topic: "lk.chat" });
      } catch (error) {
        logger.error("Failed to send tutor whiteboard exercise request", {
          error: error instanceof Error ? error.message : String(error),
        });
      }
    },
    [room]
  );

  return { selection: selection?.room === room ? selection : null, selectForPractice };
}
